import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import { DefinitionList, Panel } from '../components/Panel.jsx';
import { Resource, StatusGlyph } from '../components/StateViews.jsx';
import { Tag } from '../components/StatusPill.jsx';
import LogsPanel from '../components/LogsPanel.jsx';
import SlotCard from '../components/SlotCard.jsx';
import PromoteDialog from '../components/PromoteDialog.jsx';
import CustomDomains from '../components/CustomDomains.jsx';
import { formatNumber, formatRelative, formatTimestamp, subuidWidth } from '../lib/format.js';
import SitePreview from '../components/SitePreview.jsx';
import PinButton from '../components/PinButton.jsx';

export default function SiteDetail() {
  const { name } = useParams();
  const { user } = useAuth();
  const load = useCallback((signal) => endpoints.site(name, signal), [name]);
  const state = useResource(load, [name]);
  const platformState = useResource(useCallback((signal) => endpoints.platform(signal), []), []);
  const site = state.data;

  return (
    <div className="page">
      <PageHeader
        eyebrow={
          <>
            <Link className="crumb" to="/sites">Sites</Link>
            <span className="crumb__sep">/</span>
            <span className="mono">{name}</span>
          </>
        }
        title={<span className="mono">{name}</span>}
        lede={site ? (
          <span className="pagehead__badges">
            <Tag>{site.kind}</Tag>
            {site.tenant ? <Tag>tenant: {site.tenant}</Tag> : null}
          </span>
        ) : null}
        actions={
          <>
            {site ? <PinButton siteName={site.name} showLabel /> : null}
            <button type="button" className="btn" onClick={state.refresh}>Refresh</button>
          </>
        }
      />

      <Resource
        state={state}
        loadingLabel={`Loading ${name}…`}
        loadingRows={5}
        errorTitle={`Could not load ${name}`}
        emptyTitle="Site not found"
        emptyBody="The registry returned no record for this name."
      >
        {(data) => (
          <SiteBody
            site={data}
            isAdmin={user?.role === 'admin'}
            publicIp={platformState.data?.publicIp}
            onRefresh={state.refresh}
          />
        )}
      </Resource>

      <LogsPanel siteName={name} />
    </div>
  );
}

function SiteBody({ site, isAdmin, publicIp, onRefresh }) {
  const [promoteOpen, setPromoteOpen] = useState(false);
  const preview = site.slots?.preview || {};
  const live = site.slots?.live || {};
  const envKeys = site.envKeys || { preview: [], live: [] };
  const releases = site.releases || [];
  const width = subuidWidth(site.subuidRange);

  return (
    <>
      <div className="grid grid--2 slot-grid">
        <SlotCard slotName="preview" slot={preview} />
        <SlotCard
          slotName="live"
          slot={live}
          preview={preview}
          isAdmin={isAdmin}
          onPromote={() => setPromoteOpen(true)}
        />
      </div>

      <PromoteDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        siteName={site.name}
        previewVersion={preview.version}
        liveVersion={live.version}
        onPromoted={onRefresh}
      />

      <SitePreview site={site} />

      <div className="grid grid--2">
        <Panel title="Overview" subtitle="Runtime and per-container limits" id="overview">
          <DefinitionList
            items={[
              { label: 'Kind', value: site.kind },
              { label: 'Image', value: site.image, mono: true },
              {
                label: 'Start command', value: site.startCmd, mono: true,
                hint: site.startCmd ? null : 'image default',
              },
              { label: 'Memory limit', value: site.mem, mono: true, hint: 'per deployed slot' },
              { label: 'CPU quota', value: site.cpu, mono: true, hint: 'per deployed slot' },
              {
                label: 'Created', value: formatTimestamp(site.createdAt), mono: true,
                hint: site.createdAt ? formatRelative(site.createdAt) : null,
              },
            ]}
          />
        </Panel>

        <Panel
          title="Isolation"
          subtitle="OS user, user namespace, and containers"
          id="isolation"
          className="panel--feature"
        >
          <DefinitionList
            items={[
              { label: 'OS user', value: site.osUser, mono: true },
              { label: 'Home', value: site.home, mono: true, hint: 'mode 0750' },
              { label: 'Isolation slot', value: site.isoSlot, mono: true, hint: 'subuid allocation index' },
              {
                label: 'subuid / subgid range', value: site.subuidRange, mono: true,
                hint: width ? `${formatNumber(width)} ids` : null,
              },
            ]}
          />
          <p className="panel__note">
            Both deployment slots share <span className="mono">{site.osUser}</span> and the release
            store, while each deployed slot runs in its own rootless container.
          </p>
        </Panel>
      </div>

      <CustomDomains
        siteName={site.name}
        domains={live.domains || []}
        publicIp={publicIp}
        isAdmin={isAdmin}
        onChanged={onRefresh}
      />

      <Panel
        title="Environment"
        subtitle="Keys only, isolated per deployment slot"
        id="environment"
        footer={
          <span className="panel__foot-note">
            <StatusGlyph tone="neutral" shape="hollow" />
            Values are never returned. Secrets live in <span className="mono">env-preview</span> and{' '}
            <span className="mono">env-live</span> at mode <span className="mono">0600</span>.
          </span>
        }
      >
        <div className="env-slots">
          {['preview', 'live'].map((slot) => (
            <section key={slot}>
              <h3>{slot}</h3>
              {(envKeys[slot] || []).length === 0 ? (
                <p className="state__body">No variables set.</p>
              ) : (
                <ul className="keylist">
                  {envKeys[slot].map((key) => (
                    <li className="keylist__item" key={key}>
                      <span className="mono">{key}</span>
                      <span className="keylist__redacted mono" aria-label="value not retrievable">••••••</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </Panel>

      <Panel title="Releases" subtitle="Slot symlinks pin retained release bytes" id="releases">
        {releases.length === 0 ? (
          <p className="state__body">Nothing has been deployed yet.</p>
        ) : (
          <div className="tablewrap">
            <table className="table">
              <thead><tr><th scope="col">Version</th><th scope="col">Created</th><th scope="col">Slots</th></tr></thead>
              <tbody>
                {releases.map((release) => (
                  <tr key={release.version} className={release.slots?.length ? 'is-active-row' : undefined}>
                    <th scope="row" className="mono">{release.version}</th>
                    <td className="mono nowrap">
                      {formatTimestamp(release.createdAt)}
                      <span className="table__sub">{formatRelative(release.createdAt)}</span>
                    </td>
                    <td>
                      {release.slots?.length ? release.slots.map((slot) => <Tag key={slot}>{slot}</Tag>) : (
                        <span className="is-muted">Superseded</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
